/**
 * SearchBar Component
 *
 * Search input with 300ms debounce for filtering documents.
 * Prevents excessive API calls during rapid typing.
 *
 * @module features/dashboard/components/SearchBar
 * @see SPEC-016 Requirements 8.1, 8.3
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/cn';

// ============================================================================
// Constants
// ============================================================================

/** Default debounce delay in milliseconds */
export const DEFAULT_DEBOUNCE_MS = 300;

// ============================================================================
// Variants
// ============================================================================

/**
 * SearchBar variants using CVA
 */
const searchBarVariants = cva(
  [
    'flex items-center gap-2',
    'border-2 border-linkode-text rounded-linkode',
    'bg-white/80',
    'transition-all duration-200',
    'focus-within:bg-white focus-within:ring-2 focus-within:ring-linkode-teal',
  ],
  {
    variants: {
      size: {
        sm: 'px-3 py-2',
        md: 'px-4 py-3',
        lg: 'px-5 py-4',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
    },
    defaultVariants: {
      size: 'md',
      fullWidth: false,
    },
  }
);

/** Exported type for SearchBar variants */
export type SearchBarVariants = VariantProps<typeof searchBarVariants>;

// ============================================================================
// Types
// ============================================================================

export interface SearchBarProps extends SearchBarVariants {
  /** Current search value */
  value?: string;
  /** Callback when search value changes (debounced) */
  onSearch: (query: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Additional class names */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Auto focus on mount */
  autoFocus?: boolean;
}

// ============================================================================
// Debounce Hook
// ============================================================================

/**
 * Custom hook for debouncing a value
 *
 * @param value - Value to debounce
 * @param delay - Debounce delay in milliseconds
 * @returns Debounced value
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================================================
// Component
// ============================================================================

/**
 * SearchBar Component
 *
 * Features:
 * - 300ms debounce to prevent excessive API calls
 * - Clear button when input has value
 * - Search icon
 * - Keyboard support (Escape to clear)
 *
 * @example
 * ```tsx
 * <SearchBar
 *   value={searchQuery}
 *   onSearch={(query) => setSearchQuery(query)}
 *   placeholder="Search documents..."
 * />
 * ```
 *
 * @see Requirements 8.1, 8.3
 */
export const SearchBar: React.FC<SearchBarProps> = ({
  value = '',
  onSearch,
  placeholder = 'Search...',
  debounceMs = DEFAULT_DEBOUNCE_MS,
  size,
  fullWidth,
  className,
  disabled = false,
  autoFocus = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedValue = useDebounce(inputValue, debounceMs);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Call onSearch when debounced value changes
  useEffect(() => {
    onSearch(debouncedValue);
  }, [debouncedValue, onSearch]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setInputValue('');
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClear();
      }
    },
    [handleClear]
  );

  return (
    <div className={cn(searchBarVariants({ size, fullWidth }), className)}>
      {/* Search Icon */}
      <svg
        className="w-5 h-5 text-linkode-text/50 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={cn(
          'flex-1 bg-transparent outline-none font-mono',
          'placeholder:text-linkode-text/40',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
        aria-label="Search documents"
      />

      {/* Clear Button */}
      {inputValue && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          className={cn(
            'p-1 rounded-full',
            'text-linkode-text/50 hover:text-linkode-text',
            'hover:bg-linkode-bg-light',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
          )}
          aria-label="Clear search"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

// Export variants for testing
export { searchBarVariants };
